import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileText, X } from 'lucide-react';
import {
  type CandidatePipelineStepId,
  type JobApplication,
  type JobApplicationStatus,
  type JobOfferLetter,
  type JobPosting,
} from '../../../types/hiring';
import { getJobApplicationResumeUrl, updateJobApplication } from '../../../utils/hiringFirestore';
import { formatResumeFileSize, isHiringResumePdf } from '../../../utils/hiringResumeViewer';
import { subscribePublishedHandbookPolicies } from '../../../utils/handbookPoliciesFirestore';
import {
  applicationStatusForTrailSelect,
  CANDIDATE_STATUS_SELECT_OPTIONS,
  candidateStatusSelectValue,
  candidateTrailBadgeLabel,
  deriveCandidatePipeline,
  type CandidateStatusSelectValue,
} from '../../../utils/jobOfferLetter';
import { subscribeLatestJobOffer } from '../../../utils/jobOfferLettersFirestore';
import CandidatePipeline from './CandidatePipeline';
import JobApplicationComments from './JobApplicationComments';
import JobApplicationResumeModal from './JobApplicationResumeModal';
import JobOfferPane from './JobOfferPane';

export type HiringResumeViewerOpen = {
  url: string;
  title: string;
  documentId: string;
  leadName?: string;
};

type Props = {
  application: JobApplication;
  posting?: JobPosting | null;
  reviewerUid: string;
  highlightTeamCommentId?: string | null;
  onTeamCommentConsumed?: () => void;
  highlightPdfCommentId?: string | null;
  onOpenResumePin?: (opts: { annotationId: string; pageNumber?: number }) => void;
  onStatusChange?: (applicationId: string, status: JobApplicationStatus) => void;
  /** Hired roster uses "Manage" — the candidate list keeps "Manage candidate". */
  manageLabel?: string;
};

const JobApplicationDetail: React.FC<Props> = ({
  application,
  posting,
  reviewerUid,
  highlightTeamCommentId,
  onTeamCommentConsumed,
  highlightPdfCommentId,
  onOpenResumePin,
  onStatusChange,
  manageLabel = 'Manage candidate',
}) => {
  const [boundApplicationId, setBoundApplicationId] = useState(application.id);
  const [status, setStatus] = useState<JobApplicationStatus>(application.status);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [resumeBusy, setResumeBusy] = useState<'view' | 'download' | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resumePreview, setResumePreview] = useState<{ url: string } | null>(null);
  const [offer, setOffer] = useState<JobOfferLetter | null>(null);
  const [composeOffer, setComposeOffer] = useState(false);
  const [requiredPolicyCount, setRequiredPolicyCount] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (application.id !== boundApplicationId) {
    setBoundApplicationId(application.id);
    setStatus(application.status);
    setSaveError(null);
    setSaveOk(false);
    setResumeError(null);
    setResumeBusy(null);
    setResumePreview(null);
    setComposeOffer(false);
    setDetailsOpen(false);
  }

  useEffect(() => {
    return subscribeLatestJobOffer(application.id, setOffer);
  }, [application.id]);

  useEffect(() => {
    return subscribePublishedHandbookPolicies(
      list => setRequiredPolicyCount(list.length),
      () => setRequiredPolicyCount(0),
      { userId: reviewerUid },
    );
  }, [reviewerUid]);

  useEffect(() => {
    if (highlightTeamCommentId || highlightPdfCommentId) setDetailsOpen(true);
  }, [highlightTeamCommentId, highlightPdfCommentId]);

  useEffect(() => {
    if (!detailsOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !resumePreview) setDetailsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailsOpen, resumePreview]);

  const pipeline = useMemo(
    () => deriveCandidatePipeline({ application, offer, requiredPolicyCount }),
    [application, offer, requiredPolicyCount],
  );

  const openDetails = (id?: CandidatePipelineStepId) => {
    if (id === 'decision' || id === 'offerLetter' || id === 'agreement' || id === 'onboarding' || id === 'startDate') {
      if (!offer || offer.status === 'draft') setComposeOffer(true);
    }
    setDetailsOpen(true);
  };

  const resume = application.resume;
  const resumeIsPdf = isHiringResumePdf(resume);
  const resumeSize = formatResumeFileSize(resume?.sizeBytes || 0);
  const resumeKind = resumeIsPdf ? 'PDF' : (resume?.fileName || '').toLowerCase().endsWith('.doc') ? 'DOC' : 'DOCX';
  const fullName = `${application.firstName} ${application.lastName}`.trim();

  const resolveResumeUrl = async (): Promise<string> => {
    if (!resume?.storagePath) throw new Error('No resume on file');
    return getJobApplicationResumeUrl(resume.storagePath);
  };

  const viewResume = async () => {
    if (!resumeIsPdf) return;
    setResumeError(null);
    setResumeBusy('view');
    try {
      const url = await resolveResumeUrl();
      setResumePreview({ url });
    } catch (e) {
      setResumeError((e as Error).message || 'Could not open resume');
    } finally {
      setResumeBusy(null);
    }
  };

  const downloadResume = async () => {
    setResumeError(null);
    setResumeBusy('download');
    try {
      const url = await resolveResumeUrl();
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = resume?.fileName || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setResumeError((e as Error).message || 'Could not download resume');
    } finally {
      setResumeBusy(null);
    }
  };

  const saveStatus = async (nextStatus: JobApplicationStatus) => {
    const applicationId = application.id;
    setStatus(nextStatus);
    onStatusChange?.(applicationId, nextStatus);
    setSaving(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      await updateJobApplication(applicationId, {
        status: nextStatus,
        reviewedBy: reviewerUid,
      });
      setSaveOk(true);
    } catch (e) {
      setSaveError((e as Error).message || 'Could not save status');
    } finally {
      setSaving(false);
    }
  };

  const offerNote = offer?.status === 'signed'
    ? 'Agreement signed'
    : offer?.status === 'sent'
      ? 'Offer sent'
      : offer?.status === 'draft'
        ? 'Offer draft'
        : null;

  const editor = (
    <>
      <div className="people-hiring-field" style={{ marginTop: 4 }}>
        <label htmlFor={`hiring-status-${application.id}`}>Status</label>
        <select
          id={`hiring-status-${application.id}`}
          value={candidateStatusSelectValue(status, pipeline)}
          onChange={e => {
            const next = applicationStatusForTrailSelect(e.target.value as CandidateStatusSelectValue);
            if (next) void saveStatus(next);
          }}
          disabled={saving}
        >
          {CANDIDATE_STATUS_SELECT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value} disabled={!opt.staffSettable}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {saveError && <p className="people-hiring-resume-error">{saveError}</p>}
      {saveOk && !saveError && (
        <p className="people-hiring-muted" style={{ margin: '4px 0 0' }}>Status saved</p>
      )}

      <div className="people-hiring-resume">
        <div className="people-hiring-resume-meta">
          <span className="people-hiring-resume-icon" aria-hidden>
            <FileText size={18} />
          </span>
          <div>
            <strong>{resume?.fileName || 'Resume'}</strong>
            <div className="people-hiring-muted">
              {resumeKind}
              {resumeSize ? ` · ${resumeSize}` : ''}
            </div>
          </div>
        </div>
        <div className="people-hiring-resume-actions">
          {resumeIsPdf && (
            <button
              type="button"
              className="pm-btn pm-btn-primary"
              onClick={() => void viewResume()}
              disabled={resumeBusy !== null || !resume?.storagePath}
            >
              {resumeBusy === 'view' ? 'Opening…' : 'View resume'}
            </button>
          )}
          <button
            type="button"
            className="pm-btn pm-btn-secondary"
            onClick={() => void downloadResume()}
            disabled={resumeBusy !== null || !resume?.storagePath}
          >
            <Download size={14} />
            {resumeBusy === 'download' ? 'Downloading…' : 'Download'}
          </button>
        </div>
        {!resumeIsPdf && (
          <p className="people-hiring-muted" style={{ margin: 0 }}>
            Word resumes can be downloaded. PDF files open in a viewer here.
          </p>
        )}
      </div>
      {resumeError && <p className="people-hiring-resume-error">{resumeError}</p>}

      {application.heardFrom ? (
        <div className="people-hiring-field">
          <label>Where did you hear from us</label>
          <div className="people-hiring-detail-statement">{application.heardFrom}</div>
        </div>
      ) : null}

      {application.statement ? (
        <div className="people-hiring-field">
          <label>Applicant statement</label>
          <div className="people-hiring-detail-statement">{application.statement}</div>
        </div>
      ) : null}

      <JobOfferPane
        key={application.id}
        application={application}
        posting={posting}
        offer={offer}
        composeOpen={composeOffer}
        onRequestCompose={() => setComposeOffer(true)}
      />

      <JobApplicationComments
        applicationId={application.id}
        applicantName={fullName}
        highlightCommentId={highlightTeamCommentId}
        onHighlightConsumed={onTeamCommentConsumed}
        highlightPdfCommentId={highlightPdfCommentId}
        onOpenResumePin={onOpenResumePin}
      />
    </>
  );

  return (
    <div className="people-hiring-detail" data-application-id={application.id}>
      <div className="people-hiring-summary">
        <div className="people-hiring-summary-top">
          <h4>{fullName}</h4>
          <span className={`people-hiring-status people-hiring-status--${status === 'new' ? 'published' : status === 'rejected' ? 'closed' : 'draft'}`}>
            {candidateTrailBadgeLabel(status, pipeline)}
          </span>
        </div>
        <p className="people-hiring-muted people-hiring-summary-meta">
          {application.jobTitle || 'No job'}
          {application.email ? ` · ${application.email}` : ''}
          {application.phone ? ` · ${application.phone}` : ''}
          {offerNote ? ` · ${offerNote}` : ''}
        </p>
        <CandidatePipeline steps={pipeline} onSelect={openDetails} />
        <div className="people-hiring-summary-actions">
          <button type="button" className="ops-new-btn" onClick={() => openDetails()}>
            {manageLabel}
          </button>
          {resumeIsPdf && (
            <button
              type="button"
              className="pm-btn pm-btn-secondary"
              onClick={() => void viewResume()}
              disabled={resumeBusy !== null || !resume?.storagePath}
            >
              {resumeBusy === 'view' ? 'Opening…' : 'View resume'}
            </button>
          )}
        </div>
        {resumeError && <p className="people-hiring-resume-error">{resumeError}</p>}
      </div>

      {detailsOpen && (
        <div className="people-hiring-modal-backdrop" onClick={() => setDetailsOpen(false)}>
          <div
            className="people-hiring-modal people-hiring-candidate-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-labelledby="hiring-section-basics"
          >
            <div className="people-hiring-modal-header">
              <div>
                <h3 id="hiring-section-basics">{fullName}</h3>
                <p className="people-hiring-muted" style={{ margin: '4px 0 0' }}>
                  {application.jobTitle || 'No job'}
                  {application.email ? (
                    <> · <a href={`mailto:${application.email}`}>{application.email}</a></>
                  ) : null}
                </p>
              </div>
              <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setDetailsOpen(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="people-hiring-modal-body people-hiring-candidate-modal-body">
              {editor}
            </div>
          </div>
        </div>
      )}

      {resumePreview && (
        <JobApplicationResumeModal
          url={resumePreview.url}
          fileName={resume?.fileName || 'resume.pdf'}
          title={`${fullName} — resume`}
          onDownload={() => void downloadResume()}
          onClose={() => setResumePreview(null)}
        />
      )}
    </div>
  );
};

export default JobApplicationDetail;
